// modules/approvals/components/ApproverPaymentSplits.jsx
//
// The payment breakdown, as the APPROVER sees it on the Approve action.
//
// A bank transfer's UTR does not exist when the maker records the payment — it
// exists only once the money has actually moved, which is this approver's act.
// So the maker submits modes and amounts, and the UTR is collected here, against
// the split it belongs to, before the payment is disbursed.
//
// The approver also picks HOW each bank transfer was sent — IMPS or NEFT. Like the
// UTR, only the person who moved the money knows it, and it is what the voucher
// prints in its Mode column, because "Bank Transfer" is not what the customer sees
// on their statement.
//
// The approver may also re-slice the BANK portion — the transfer really went out
// as two payments, or partly by UPI. They may NOT change the total, and may not
// touch the cash rows: cash and bank were routed separately when the payment was
// recorded (different ledgers, different approval limits), so that boundary is
// fixed here and re-checked on the server.

import React from 'react';
import {
  Row, Col, Select, InputNumber, Input, Button, Typography, Alert, Tag, Space,
} from 'antd';
import { PlusOutlined, DeleteOutlined, BankOutlined, WalletOutlined } from '@ant-design/icons';
import ApproverBeneficiaryCard from './ApproverBeneficiaryCard';

const { Text } = Typography;

const TOLERANCE = 0.5;

const MODE_LABEL = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank Transfer',
  UPI: 'UPI',
  CHEQUE: 'Cheque',
};

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 2,
  }).format(v || 0);

const round2 = (n) => parseFloat((Number(n) || 0).toFixed(2));

const sumOf = (rows) => round2((rows || []).reduce((s, r) => s + (Number(r?.amount) || 0), 0));

export const isBankMode = (mode, bankModes) => (bankModes || []).includes(mode);

// Defaults keep the component working against a backend that predates the rail.
const railsOf = (data) => data?.bankRails || ['IMPS', 'NEFT'];
const railModesOf = (data) => data?.railModes || ['BANK_TRANSFER'];
const needsRail = (mode, data) => railModesOf(data).includes(mode);

/**
 * The same rules the server enforces in approverSplitService — duplicated here so
 * the approver sees the problem before submitting, not as a rejected request.
 * The server remains the authority.
 *
 * @returns {string[]} human-readable problems; empty means good to approve
 */
export const validateSplits = (splits, data) => {
  if (!data || data.kind !== 'PAYMENT_SPLITS') return [];
  const bankModes = data.bankModes || [];
  const rows = splits || [];
  const errors = [];

  if (!rows.length) return ['The payment has no breakdown to approve.'];

  rows.forEach((r, i) => {
    if (!(Number(r?.amount) > 0)) errors.push(`Row ${i + 1}: amount must be greater than ₹0.`);
  });
  if (errors.length) return errors;

  const target = round2(data.amount);
  const total = sumOf(rows);
  if (Math.abs(total - target) > TOLERANCE) {
    errors.push(`The breakdown totals ${fmt(total)} but the approved amount is ${fmt(target)}.`);
  }

  const bankTarget = sumOf((data.splits || []).filter((s) => isBankMode(s.mode, bankModes)));
  const bankTotal = sumOf(rows.filter((r) => isBankMode(r.mode, bankModes)));
  if (Math.abs(bankTarget - bankTotal) > TOLERANCE) {
    errors.push(
      `The bank portion must add up to ${fmt(bankTarget)} — it currently adds up to ${fmt(bankTotal)}.`,
    );
  }

  if (data.referencesRequired) {
    rows.forEach((r, i) => {
      const label = `Row ${i + 1} (${MODE_LABEL[r.mode] || r.mode}, ${fmt(r.amount)})`;
      if (isBankMode(r.mode, bankModes) && !String(r.reference || '').trim()) {
        errors.push(`${label}: enter the UTR / reference.`);
      }
      if (needsRail(r.mode, data) && !String(r.bankRail || '').trim()) {
        errors.push(`${label}: select how it was sent (${railsOf(data).join(' or ')}).`);
      }
    });
  }

  return errors;
};

const ApproverPaymentSplits = ({ data, value, onChange, disabled = false }) => {
  if (!data || data.kind !== 'PAYMENT_SPLITS') return null;

  const bankModes = data.bankModes || [];
  const rows = value || [];
  const cashRows = rows.filter((r) => !isBankMode(r.mode, bankModes));
  const bankTarget = sumOf((data.splits || []).filter((s) => isBankMode(s.mode, bankModes)));
  const bankTotal = sumOf(rows.filter((r) => isBankMode(r.mode, bankModes)));
  const bankRemaining = round2(bankTarget - bankTotal);
  const bankBalanced = Math.abs(bankRemaining) <= TOLERANCE;

  // Who is being paid, above everything else: the approver is the one moving the
  // money, so the payee and the account it goes to belong next to the UTR box.
  const beneficiary = (
    <ApproverBeneficiaryCard data={data.beneficiary} hasBankPortion={bankTarget > 0} />
  );

  // Nothing settles through a bank — a pure cash payout carries no UTR at all.
  if (!bankTarget) {
    return (
      <div style={{ marginBottom: 16 }}>
        {beneficiary}
        <Alert
          type="info"
          showIcon
          icon={<WalletOutlined />}
          message="Cash payout"
          description={`${fmt(data.amount)} in cash — no UTR to record. It is paid from the branch cash drawer on approval.`}
        />
      </div>
    );
  }

  const editable = !disabled && data.editable !== false;
  // Named when the template flagged a specific step; otherwise it is simply the
  // step that finishes the workflow.
  const captureStepLabel = data.captureStepName ? ` "${data.captureStepName}"` : ' final approval';

  const updateRow = (index, patch) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const addBankRow = () => {
    // Pre-fill with whatever is still unaccounted for, which is the common case:
    // one row was split into two and the second takes the remainder.
    onChange([
      ...rows,
      {
        mode: bankModes[0] || 'BANK_TRANSFER',
        amount: bankRemaining > 0 ? bankRemaining : null,
        reference: '',
        bankRail: '',
      },
    ]);
  };

  const bankRowCount = rows.filter((r) => isBankMode(r.mode, bankModes)).length;

  return (
    <div style={{ marginBottom: 16 }}>
      {beneficiary}
      <Alert
        type={editable ? 'warning' : 'info'}
        showIcon
        icon={<BankOutlined />}
        style={{ marginBottom: 12 }}
        message={
          editable
            ? 'Enter the transfer reference'
            : data.pastCaptureStep
              ? 'Transfer reference already recorded'
              : 'Transfer reference not recorded yet'
        }
        description={
          editable ? (
            <>
              This payment of {fmt(data.amount)}
              {data.paymentId ? ` (${data.paymentId})` : ''} is yours to release. For each bank
              payment you made, record how it was sent ({railsOf(data).join(' or ')}) and its UTR.
              You can split the bank portion across several transfers, but the total and the cash
              portion cannot be changed here.
            </>
          ) : data.pastCaptureStep ? (
            <>
              {fmt(data.amount)}
              {data.paymentId ? ` (${data.paymentId})` : ''} — the references below were entered
              at the{captureStepLabel} step. They are shown for your sign-off and cannot be
              changed here.
            </>
          ) : (
            <>
              {fmt(data.amount)}
              {data.paymentId ? ` (${data.paymentId})` : ''} has not been transferred yet. The UTR
              is recorded at the{captureStepLabel} step, so there is nothing to enter now.
            </>
          )
        }
      />

      {cashRows.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {cashRows.map((r, i) => (
            <Row key={`cash-${i}`} gutter={8} align="middle" style={{ marginBottom: 6 }}>
              <Col flex="130px">
                <Tag icon={<WalletOutlined />} color="gold" style={{ margin: 0 }}>
                  {MODE_LABEL[r.mode] || r.mode}
                </Tag>
              </Col>
              <Col flex="150px">
                <Text strong>{fmt(r.amount)}</Text>
              </Col>
              <Col flex="auto">
                <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                  Paid from branch cash — no reference
                </Text>
              </Col>
            </Row>
          ))}
        </div>
      )}

      {rows.map((r, index) => {
        if (!isBankMode(r.mode, bankModes)) return null;
        return (
          <Row key={`bank-${index}`} gutter={8} align="middle" style={{ marginBottom: 8 }}>
            <Col flex="130px">
              <Select
                size="small"
                value={r.mode}
                disabled={!editable}
                style={{ width: '100%' }}
                onChange={(mode) => updateRow(index, {
                  mode,
                  // A rail belongs to a bank transfer only — switching to UPI or a
                  // cheque must not carry a stale "IMPS" into the Mode column.
                  bankRail: needsRail(mode, data) ? r.bankRail || '' : '',
                })}
                options={bankModes.map((m) => ({ value: m, label: MODE_LABEL[m] || m }))}
              />
            </Col>
            {needsRail(r.mode, data) && (
              <Col flex="104px">
                <Select
                  size="small"
                  value={r.bankRail || undefined}
                  disabled={!editable}
                  style={{ width: '100%' }}
                  placeholder="Sent via"
                  status={data.referencesRequired && !r.bankRail ? 'error' : undefined}
                  onChange={(bankRail) => updateRow(index, { bankRail })}
                  options={railsOf(data).map((rail) => ({ value: rail, label: rail }))}
                />
              </Col>
            )}
            <Col flex="150px">
              <InputNumber
                size="small"
                min={0}
                precision={2}
                prefix="₹"
                style={{ width: '100%' }}
                value={r.amount}
                disabled={!editable}
                placeholder="Amount"
                onChange={(amount) => updateRow(index, { amount })}
              />
            </Col>
            <Col flex="auto">
              <Input
                size="small"
                value={r.reference}
                disabled={!editable}
                status={data.referencesRequired && !String(r.reference || '').trim() ? 'error' : undefined}
                placeholder={r.mode === 'CHEQUE' ? 'Cheque number' : 'UTR / reference'}
                onChange={(e) => updateRow(index, { reference: e.target.value })}
              />
            </Col>
            <Col flex="32px">
              {editable && bankRowCount > 1 && (
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeRow(index)}
                />
              )}
            </Col>
          </Row>
        );
      })}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px 8px',
          marginBottom: 8,
          borderRadius: 4,
          background: bankBalanced ? '#f6ffed' : '#fff7e6',
          border: `1px solid ${bankBalanced ? '#b7eb8f' : '#ffd591'}`,
        }}
      >
        <Text style={{ fontSize: 12 }}>
          Bank total: <strong>{fmt(bankTotal)}</strong>
        </Text>
        <Text style={{ fontSize: 12, color: bankBalanced ? '#3f8600' : '#d48806' }}>
          {bankBalanced
            ? `✓ Matches the approved bank amount (${fmt(bankTarget)})`
            : bankRemaining > 0
              ? `Unaccounted: ${fmt(bankRemaining)}`
              : `Over by ${fmt(Math.abs(bankRemaining))}`}
        </Text>
      </div>

      {editable && (
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          style={{ width: '100%' }}
          onClick={addBankRow}
        >
          Paid in more than one transfer? Add another
        </Button>
      )}

      {!editable && (
        <Space size={4}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {data.pastCaptureStep
              ? `Recorded at the${captureStepLabel} step — read-only here.`
              : `Collected at the${captureStepLabel} step, not this one.`}
          </Text>
        </Space>
      )}
    </div>
  );
};

export default ApproverPaymentSplits;
